import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useSettings } from '../context/SettingsContext';
import Modal from './ui/Modal';
import SettingRow from './ui/SettingRow';
import { containerVariants, itemVariants, headerVariants } from '../utils/animations';

function SettingsModal({ onClose }) {
  const {
    soundEnabled,
    toggleSound,
    showTimer,
    toggleShowTimer,
    showMoveCount,
    toggleShowMoveCount,
    showPreview,
    toggleShowPreview,
    confettiEnabled,
    toggleConfetti,
    theme,
    toggleTheme,
  } = useSettings();

  // Icons
  const SoundIcon = (
    <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
    </svg>
  );

  const TimerIcon = (
    <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const MovesIcon = (
    <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
    </svg>
  );

  const PreviewIcon = (
    <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );

  const ConfettiIcon = (
    <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );

  const DarkModeIcon = (
    <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  );

  return (
    <Modal onClose={onClose} maxWidth="max-w-sm">
      {/* Header */}
      <motion.div
        className="flex justify-between items-center mb-6"
        variants={headerVariants}
        initial="hidden"
        animate="visible"
      >
        <h2 className="text-xl font-semibold text-puzzle-text">Settings</h2>
        <button
          onClick={onClose}
          type="button"
          className="p-1 text-puzzle-text-secondary hover:text-puzzle-text transition-colors"
          aria-label="Close settings"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Display Settings Section */}
        <motion.div className="bg-puzzle-background rounded-xl p-4" variants={itemVariants}>
          <div className="divide-y divide-puzzle-border">
            <SettingRow
              icon={SoundIcon}
              label="Connection Sound"
              enabled={soundEnabled}
              onToggle={toggleSound}
            />
            <SettingRow
              icon={TimerIcon}
              label="Show Timer"
              enabled={showTimer}
              onToggle={toggleShowTimer}
            />
            <SettingRow
              icon={MovesIcon}
              label="Show Move Count"
              enabled={showMoveCount}
              onToggle={toggleShowMoveCount}
            />
            <SettingRow
              icon={PreviewIcon}
              label="Show Preview Image"
              enabled={showPreview}
              onToggle={toggleShowPreview}
            />
          </div>
        </motion.div>

        {/* Effects Section */}
        <motion.div className="bg-puzzle-background rounded-xl p-4 mt-4" variants={itemVariants}>
          <SettingRow
            icon={ConfettiIcon}
            label="Confetti on Completion"
            enabled={confettiEnabled}
            onToggle={toggleConfetti}
          />
        </motion.div>

        {/* Appearance Section */}
        <motion.div className="bg-puzzle-background rounded-xl p-4 mt-4" variants={itemVariants}>
          <SettingRow
            icon={DarkModeIcon}
            label="Dark Mode"
            enabled={theme === 'dark'}
            onToggle={toggleTheme}
          />
        </motion.div>

        {/* Footer */}
        <motion.div className="mt-6" variants={itemVariants}>
          <motion.button
            onClick={onClose}
            type="button"
            className="w-full py-3 px-4 bg-puzzle-blue text-white font-medium rounded-full hover:bg-blue-600 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Done
          </motion.button>
        </motion.div>
      </motion.div>
    </Modal>
  );
}

SettingsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default SettingsModal;
